import { useEffect, useRef } from "react";
import type { KeyboardEvent } from "react";
import type { DropdownChangeEvent } from "primereact/dropdown";

import { SECOES_MENU_ATRIBUTO_ATIVO } from "./secoes-menu.constants";
import type { SecoesMenuProps } from "./types-secoes-menu";

/** Itens com o estado ativo já decidido e as opções do seletor do mobile. */
export function useSecoesMenu({
  items,
  activeId,
  onSelect,
  isMobile,
  label,
  className,
  acompanharAtivo = false,
  testId,
  selectTestId,
}: SecoesMenuProps) {
  const menuRef = useRef<HTMLElement>(null);

  // Rola só o próprio menu (scrollIntoView rolaria também a modal e a página) até o item ativo caber na área visível,
  // com o respiro do padding do menu entre o item e a borda.
  useEffect(() => {
    if (!acompanharAtivo || isMobile) return;
    const menu = menuRef.current;
    const item = menu?.querySelector<HTMLElement>(`[${SECOES_MENU_ATRIBUTO_ATIVO}]`);
    if (!menu || !item) return;

    const respiro = Number.parseFloat(getComputedStyle(menu).paddingTop) || 0;
    const area = menu.getBoundingClientRect();
    const alvo = item.getBoundingClientRect();
    if (alvo.top < area.top + respiro) menu.scrollTop -= area.top + respiro - alvo.top;
    else if (alvo.bottom > area.bottom - respiro) {
      menu.scrollTop += alvo.bottom - (area.bottom - respiro);
    }
  }, [activeId, acompanharAtivo, isMobile]);

  return {
    menuRef,
    isMobile,
    label,
    className,
    testId,
    selectTestId,
    activeId,
    itens: items.map((item) => ({
      id: item.id,
      title: item.title,
      testId: item.testId,
      ativo: item.id === activeId,
      ariaCurrent: item.id === activeId ? ("true" as const) : undefined,
      marcaAtivo: item.id === activeId ? { [SECOES_MENU_ATRIBUTO_ATIVO]: "" } : undefined,
    })),
    opcoes: items.map((item) => ({ label: item.title, value: item.id })),
    // Esc com a lista aberta (aria-expanded do gatilho) só fecha a lista: não chega ao diálogo que contém o menu.
    protegerEsc: (event: KeyboardEvent<HTMLElement>) => {
      const aberto = event.currentTarget.querySelector('[aria-expanded="true"]') !== null;
      if (aberto && event.key === "Escape") event.nativeEvent.stopPropagation();
    },
    selecionar: (id: string) => onSelect(id),
    selecionarOpcao: (event: DropdownChangeEvent) => onSelect(event.value as string),
  };
}
